// src/Login.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Screen from './Screen';
import './Login.css';
import api from './api';

import barterfiLogoLogin from './assets/barterfi-logologin.png';
import piIcon from './assets/pi-icon.png';

import PiAuthOnlyDrawer from './components/popups/PiAuthOnlyDrawer';
import Spinner from './components/Spinner';

// ---- backend tap helper (logs phases to your backend) ----
const piTap = (phase, extra = {}) => {
  try {
    const urlBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

    if (!urlBase) return;

    const url = `${urlBase}/auth/pi-tap`;

    const meta = {
      host: window.location.host,
      href: window.location.href,
      referrer: document.referrer || null,
      ua: navigator.userAgent,
      ...extra,
    };

    const body = JSON.stringify({ phase, meta });

    if (navigator.sendBeacon) {
      const blob = new Blob([body], {
        type: 'application/json',
      });

      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {}
};

// Promise timeout helper
const withTimeout = (p, ms, label) =>
  new Promise((resolve, reject) => {
    const id = setTimeout(() => {
      piTap(`${label}-timeout`, { ms });

      reject(new Error(`${label} timeout after ${ms}ms`));
    }, ms);

    p.then((v) => {
      clearTimeout(id);
      resolve(v);
    }).catch((e) => {
      clearTimeout(id);
      reject(e);
    });
  });

// ---------- Runtime helpers ----------
const isTestnetEnv = () => {
  const net = String(process.env.REACT_APP_PI_NETWORK || '').toLowerCase();

  return net === 'testnet';
};

const openPiLauncher = () => {
  const appId = process.env.REACT_APP_PI_APP_ID || 'barterfi-testnet';

  const back = encodeURIComponent(window.location.href);

  const url = isTestnetEnv()
    ? `https://sandbox.minepi.com/app/${appId}?r=${back}`
    : `https://minepi.com/app/${appId}?r=${back}`;

  piTap('redirecting-to-launcher', { url });

  window.location.href = url;
};

// ✅ Robust detection for “already inside Pi context”
const inPiContext = () => {
  const ua = navigator.userAgent || '';
  const ref = document.referrer || '';

  if (/PiBrowser/i.test(ua)) return true;

  if (/app-cdn\.minepi\.com/i.test(ref)) return true;

  if (/(?:^|\/\/)(?:sandbox\.)?minepi\.com/i.test(ref)) return true;

  return false;
};

const Login = ({ onLogin }) => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorBanner, setErrorBanner] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPiAuthDrawer, setShowPiAuthDrawer] = useState(false);

  // ✅ Hide admin button inside Pi Browser
  const [showAdminButton, setShowAdminButton] = useState(false);

  useEffect(() => {
    setShowAdminButton(!inPiContext());
  }, []);

  // Toggle to force showing the scope modal
  const REQUIRE_SCOPE_MODAL =
    String(process.env.REACT_APP_PI_REQUIRE_SCOPE_MODAL || '').toLowerCase() === 'true';

  // ✅ Init Pi SDK
  useEffect(() => {
    const initPi = () => {
      if (!window.Pi) {
        console.warn('⚠️ Pi SDK not found yet.');
        piTap('sdk-missing');
        return;
      }

      try {
        window.Pi.init({
          version: '2.0',
          sandbox: false,
        });

        console.log(
          `✅ Pi SDK initialized | sandbox=false | host=${window.location.hostname}`
        );

        piTap('sdk-init', {
          sandbox: false,
          hasAuthenticate: !!window.Pi.authenticate,
          hasOpenScope: !!window.Pi.openScopeAccessModal,
          hasWallet: !!window.Pi.wallet,
        });
      } catch (e) {
        piTap('sdk-init-error', {
          message: String(e?.message || e),
        });
      }
    };

    if (window.Pi) {
      piTap('sdk-present-check', {
        present: true,
      });

      initPi();

      return;
    }

    piTap('sdk-present-check', {
      present: false,
    });

    const alreadyTag = !!document.querySelector(
      'script[src*="sdk.minepi.com/pi-sdk.js"]'
    );

    if (!alreadyTag) {
      const s = document.createElement('script');

      s.src = 'https://sdk.minepi.com/pi-sdk.js';
      s.defer = true;

      s.onload = () => {
        piTap('sdk-fallback-loaded');
        initPi();
      };

      s.onerror = () => {
        piTap('sdk-fallback-error');
      };

      document.head.appendChild(s);
    }

    let tries = 0;

    const t = setInterval(() => {
      tries += 1;

      if (window.Pi) {
        clearInterval(t);

        piTap('sdk-present-late', { tries });

        initPi();
      } else if (tries > 40) {
        clearInterval(t);

        console.warn('⚠️ Pi SDK still not available after ~8s.');

        piTap('sdk-present-timeout');
      }
    }, 200);

    return () => clearInterval(t);
  }, []);

  // ⏱️ Auto-hide errors
  useEffect(() => {
    if (!errorBanner) return;

    const timer = setTimeout(() => {
      setErrorBanner('');
    }, 5000);

    return () => clearTimeout(timer);
  }, [errorBanner]);

  const validateEmail = (val) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      setErrorBanner('Please enter a valid email address.');
      return;
    }

    if (password.length < 6) {
      setErrorBanner('Password must be at least 6 characters.');
      return;
    }

    try {
      const res = await api.post('/auth/login', {
        email,
        password,
      });

      const user = res.data.user;

      localStorage.setItem('token', res.data.token);
      localStorage.setItem('barterfi_user', JSON.stringify(user));

      localStorage.removeItem('profilePosts');
      localStorage.removeItem('savedPosts');
      localStorage.removeItem('userPosts');

      onLogin(user);

      navigate('/home');
    } catch (err) {
      console.error('❌ Email login failed:', err);

      setErrorBanner(
        err.response?.data?.message || 'Login failed.'
      );
    }
  };

  const handlePiLogin = async () => {
    try {
      setIsLoading(true);

      piTap('pi-login-tap');

      // ✅ Only open launcher if NOT already in Pi context
      if (!inPiContext()) {
        piTap('opening-launcher-pre-auth', {
          reason: 'not-in-pi-context',
          testnet: isTestnetEnv(),
        });

        openPiLauncher();

        return;
      }

      if (!window.Pi) {
        piTap('no-pi-sdk-yet');

        setErrorBanner(
          'Initializing Pi… please try again in a moment.'
        );

        return;
      }

      console.log('🟡 [Pi Login] Using Pi SDK...');

      if (
        REQUIRE_SCOPE_MODAL &&
        window.Pi?.openScopeAccessModal
      ) {
        piTap('open-scope');

        try {
          await withTimeout(
            window.Pi.openScopeAccessModal([
              'username',
              'payments',
              'wallet_address',
            ]),
            10000,
            'open-scope'
          );

          piTap('scope-granted');
        } catch (e) {
          piTap('scope-failed', {
            message: String(e?.message || e),
          });
        }
      }

      piTap('authenticate-call');

      const result = await withTimeout(
        window.Pi.authenticate(
          ['username', 'payments', 'wallet_address'],
          (p) =>
            piTap('incomplete-payment-found', {
              id: p?.identifier || p?.payment_id || null,
            })
        ),
        25000,
        'authenticate'
      );

      piTap('authenticate-ok', {
        hasUser: !!result?.user,
        hasUsername: !!result?.user?.username,
      });

      if (!result.user?.username) {
        setErrorBanner(
          '⚠️ Pi username not found. Please tap "Grant Access" when prompted.'
        );

        piTap('authenticate-missing-username');

        return;
      }

      let publicKey = '';

      if (window.Pi.wallet) {
        try {
          const wallet = await withTimeout(
            window.Pi.wallet(),
            8000,
            'wallet'
          );

          publicKey = wallet?.public_key || '';

          piTap('wallet-ok', {
            hasPublicKey: !!publicKey,
          });
        } catch (e) {
          piTap('wallet-failed', {
            message: String(e?.message || e),
          });
        }
      }

      const payload = {
        user: {
          ...result.user,
          public_key: publicKey,
        },
        accessToken: result.accessToken,
      };

      piTap('posting-to-backend', {
        username: payload.user.username,
      });

      const res = await api.post('/auth/pi-auth', payload);

      const user = res.data.user;

      piTap('backend-success', {
        username: user?.username,
      });

      localStorage.setItem('token', res.data.token);

      localStorage.setItem(
        'barterfi_user',
        JSON.stringify(user)
      );

      localStorage.removeItem('profilePosts');
      localStorage.removeItem('savedPosts');
      localStorage.removeItem('userPosts');

      onLogin(user);

      navigate('/home');
    } catch (err) {
      const msg = String(err?.message || err);

      console.error('❌ Pi login failed:', msg);

      piTap('frontend-error', {
        error: msg,
      });

      if (
        msg.includes('authenticate timeout') &&
        !inPiContext()
      ) {
        openPiLauncher();
        return;
      }

      setErrorBanner(
        'Pi login failed. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Screen>
      {isLoading && <Spinner />}

      <div className="pi-login-page">
        <div className="pi-login-content">
          <img
            src={barterfiLogoLogin}
            alt="BarterFi Logo"
            className="pi-login-logo"
          />

          {errorBanner && (
            <div className="error-banner">
              {errorBanner}
            </div>
          )}

          <button
            className="pi-primary-btn"
            onClick={handlePiLogin}
          >
            <img
              src={piIcon}
              alt="Pi"
              className="pi-btn-icon"
            />
            Continue with Pi account
          </button>

          {/* ✅ Hidden inside Pi Browser */}
          {showAdminButton && (
            <button
              className="pi-admin-btn"
              onClick={() => navigate('/admin-login')}
            >
              ADMIN
            </button>
          )}

          <div className="pi-login-footer">
            Don’t have an account?{' '}
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();
                setShowPiAuthDrawer(true);
              }}
            >
              Sign up here
            </Link>
          </div>

          <PiAuthOnlyDrawer
            isOpen={showPiAuthDrawer}
            onClose={() => setShowPiAuthDrawer(false)}
          />
        </div>
      </div>
    </Screen>
  );
};

export default Login;
